import { useState } from 'react';
import { useAppSettings } from '../hooks/useAppSettings';
import { useAuthHeaders } from './useAuthHeaders';

interface RenameFileParams {
    category: string;
    oldFileName: string;
    newFileName: string;
}

export const useRenameFile = () => {
    const { settings: appSettings } = useAppSettings();
    const { getAuthHeaders } = useAuthHeaders();
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameError, setRenameError] = useState<string | null>(null);
    const [renameSuccess, setRenameSuccess] = useState(false);

    const renameFile = async ({ category, oldFileName, newFileName }: RenameFileParams): Promise<void> => {
        if (!appSettings?.api) {
            throw new Error('API settings not available');
        }

        setIsRenaming(true);
        setRenameError(null);
        setRenameSuccess(false);

        try {
            const response = await fetch(`${appSettings.api}RenameFile`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    Category: category,
                    OldFileName: oldFileName,
                    NewFileName: newFileName,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Rename file failed (${response.status}): ${errorText}`);
            }

            setRenameSuccess(true);
        } catch (error: any) {
            setRenameError(error.message || 'Failed to rename file');
            throw error;
        } finally {
            setIsRenaming(false);
        }
    };

    const clearMessages = () => {
        setRenameError(null);
        setRenameSuccess(false);
    };

    return {
        renameFile,
        isRenaming,
        renameError,
        renameSuccess,
        clearMessages,
    };
};
